import React, {useState} from "react";
import Box from "@mui/material/Box";
import {IconButton, Typography} from "@mui/material";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import Table from "@mui/material/Table";
import Paper from "@mui/material/Paper";
import {useDispatch, useSelector} from "react-redux";
import {useApi} from "../../hooks/api-hook";
import {API_SPEC} from "../../spec/apiSpec";
import {columns} from '../collection/definition';
import {orderColumns} from '../collection/definition';
import AddCardsToCollection from "../collection/AddCardsToCollection";
import Button from "@mui/material/Button";
import {Save} from "@mui/icons-material";
import {useFormik} from "formik";
import PropTypes from "prop-types";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CollectionTabs from "../collection/CollectionTabs";
import {grouper} from "../collection/collectionUtil";
import AddCardToCollectionSelects from "../collection/AddCardToCollectionSelects";
import {galleryLink} from "../../utils/linkMaker";
import {alertAction} from "../../reducers/alertReducer";
import PaginatedComponent from "../hoc/PaginatedComponent";
import {Link} from "react-router-dom";
import {useKeycloak} from "@react-keycloak/web";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AddIcon from '@mui/icons-material/Add';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import Switch from '@mui/material/Switch';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';

function TabPanel(props) {
    const {children, value, index, ...other} = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{p: 3}}>
                    <Typography>{children}</Typography>
                </Box>
            )}
        </div>
    );
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
};

export default function MyCollection() {
    const [openAdd, setOpenAdd] = React.useState(false);
    const [openAddBulk, setOpenAddBulk] = React.useState(false);
    const dispatch = useDispatch();
    const {keycloak} = useKeycloak();

    const {page, items} = useSelector((state) => state.collectionReducer)
    const {isLoading, data, api, initialized} = useApi();
    const {
        page: defaultPage,
        rowsPerPage: defaultRowsPerPage,
        offerTypeTab
    } = useSelector((state) => state.controlsReducer)

    React.useEffect(() => {
        const collection_spec = API_SPEC.COLLECTION({
            page: defaultPage,
            size: defaultRowsPerPage,
            offerTypeTab,
            grouped: false
        })
        api(collection_spec);
    }, [initialized, offerTypeTab]);


    const handleChangePage = (newPage, rowsPerPage) => {
        const card_spec = API_SPEC.COLLECTION({page: newPage, size: rowsPerPage, offerTypeTab, grouped: false})
        api(card_spec);
    };

    const handleChangeRowsPerPage = (size) => {
        const card_spec = API_SPEC.COLLECTION({page: 0, size: size, offerTypeTab, grouped: false})
        api(card_spec);
    };


    const handleAddBulkOpen = () => {
        setOpenAddBulk(true);
    };
    const handleAddBulkClose = () => {
        setOpenAddBulk(false);
    };

    const handleAddOpen = () => {
        setOpenAdd(true);
    };
    const handleAddClose = () => {
        setOpenAdd(false);
    };

    const deleteAllItemsConfirmed = async () => {
        const collection_delete = API_SPEC.COLLECTION_DELETE_ALL()
        await api(collection_delete);
        const collection_spec = API_SPEC.COLLECTION({page: 0, size: defaultRowsPerPage, offerTypeTab, grouped: false})
        api(collection_spec);
    }

    const handleDeleteAll = async (cardId) => {
        await dispatch(alertAction({
            open: true,
            text: 'Are you sure? All cards will be deleted.',
            action: () => deleteAllItemsConfirmed(cardId)
        }));
    }


    return (
        <>
            <Box sx={{marginTop: 4, marginBottom: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                <Typography variant="h4">My Orders</Typography>
                <Box>
                    <Button color="success" sx={{margin: 1}} variant={"contained"} onClick={handleAddOpen} startIcon={<AddIcon />}>Add A Card</Button>
                    <Button color="success" sx={{margin: 1}} variant={"outlined"} onClick={handleAddBulkOpen} startIcon={<PlaylistAddIcon />}>Add Multiple
                        Cards</Button>
                    <Button sx={{margin: 1}} variant={"outlined"} onClick={() => handleDeleteAll()} startIcon={<DeleteIcon />}>Delete all
                        cards</Button>
                    <Button color="info" sx={{margin: 1}} variant={"text"} component={Link} to={`/user/${keycloak.tokenParsed.preferred_username}`}  endIcon={<OpenInNewIcon />} target="_blank">Public View</Button>
                </Box>
            </Box>
            <AddCardsToCollection open={openAddBulk} handleClose={() => handleAddBulkClose()}/>
            <AddCardToCollectionSelects open={openAdd} handleClose={() => handleAddClose()}/>
            <CollectionTabs items={items} page={page} handleChangePage={handleChangePage}
                            handleChangeRowsPerPage={handleChangeRowsPerPage} DataComponent={CollectionTable}/>
        </>
    )
}


function CollectionTable({items, page, handleChangePage, handleChangeRowsPerPage, isHave}) {
    const {api} = useApi();
    const dispatch = useDispatch()
    const [edited, setEdited] = React.useState(null);
    const [grouped, setGrouped] = useState(false);
    const groupedItems = grouper(items.filter(i => i.offerType === isHave), grouped)?.sort((a, b) => a.items[0].id - b.items[0].id);

    const handleChangeGrouped = () => {
        setGrouped(!grouped)
    }

    const editRow = (card_id) => {
        setEdited(card_id)
    }

    const deleteItemConfirmed = async (cardId) => {
        let pageNumber = page.number;
        if (page.totalElements % page.size == 1) {
            pageNumber = page.number > 0 ? page.number - 1 : 0
        }
        const collection_delete = API_SPEC.COLLECTION_DELETE(cardId)
        await api(collection_delete);
        const collection_spec = API_SPEC.COLLECTION({
            page: pageNumber,
            size: page.size,
            offerTypeTab: isHave,
            grouped: false
        })
        api(collection_spec);
    }

    const deleteItem = async (cardId) => {
        await dispatch(alertAction({
            open: true,
            text: 'Are you sure? Card will be deleted.',
            action: () => deleteItemConfirmed(cardId)
        }));
    }

    const cloneItem = (cardId) => {
        const collection_clone = API_SPEC.COLLECTION_CLONE(cardId)
        api(collection_clone);
    }

    const formik = useFormik({
        initialValues: {},
        onSubmit: (values, {resetForm}) => {
            const data = {
                ...values,
                id: edited.id,
                price: values.price ? values.price.replace(',', '.').replaceAll(' ', '') * 100 : undefined
            }
            const collection_add = API_SPEC.COLLECTION_UPDATE(data)
            api(collection_add);
            resetForm();
            setEdited(null);
        },
    });

    return (
        <form onSubmit={formik.handleSubmit}>
            <FormGroup sx={{margin: 1}}>
                <FormControlLabel control={<Switch
                    checked={grouped}
                    onChange={handleChangeGrouped}
                    inputProps={{'aria-label': 'controlled'}}
                />
                } label="Group the same cards"/>
            </FormGroup>
            <PaginatedComponent size={groupedItems.length} totalElements={page.totalElements}
                                handleChangePage={handleChangePage} handleChangeRowsPerPage={handleChangeRowsPerPage}>
                <TableContainer component={Paper}>
                    <Table sx={{minWidth: 650}} aria-label="simple table">
                        <TableHead>
                            <TableRow>
                                <TableCell align="left">{orderColumns.orderId.headerName}</TableCell>
                                <TableCell align="left">{columns.set.headerName}</TableCell>
                                <TableCell align="left">{columns.type.headerName}</TableCell>
                                <TableCell align="center">{columns.condition.headerName}</TableCell>
                                <TableCell align="center">{columns.language.headerName}</TableCell>
                                <TableCell align="center">{columns.foil.headerName}</TableCell>
                                <TableCell align="left">{columns.rarity.headerName}</TableCell>
                                <TableCell align="right">{columns.quantity.headerName}</TableCell>
                                <TableCell align="right">{columns.price.headerName}</TableCell>
                                <TableCell align="left">{columns.notes.headerName}</TableCell>
                                <TableCell align="center">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {groupedItems.map(({items: i, count}) => {
                                const row = i[0]
                                const editing = edited?.id === row.id && !grouped
                                return (
                                    <TableRow
                                        key={row.id}
                                        sx={{
                                            '&:last-child td, &:last-child th': {border: 0},
                                            background: editing ? '#e9e9e9' : 'white'
                                        }}
                                    >
                                        <TableCell
                                            align="left">{orderColumns.orderId.component(row.cardId)}</TableCell>
                                        <TableCell align="left">{columns.set.component(row.setCode)}</TableCell>
                                        <TableCell align="left">{columns.type.component(row.typeLine)}</TableCell>
                                        <TableCell
                                            align="center">{columns.condition.component(row.condition, editing, formik)}</TableCell>
                                        <TableCell
                                            align="center">{columns.language.component(row.language, editing)}</TableCell>
                                        <TableCell
                                            align="center">{columns.foil.component(row.foil, editing, formik)}</TableCell>
                                        <TableCell align="left">{columns.rarity.component(row.rarity)}</TableCell>
                                        <TableCell
                                            align="right">{columns.quantity.component(count)}</TableCell>
                                        <TableCell
                                            align="right">{columns.price.component(row.price / 100, editing, formik)}</TableCell>
                                        <TableCell
                                            align="right">{columns.notes.component(row.notes, editing, formik)}</TableCell>
                                        <TableCell align="right" width={"168"}>
                                            {editing ?
                                                <IconButton type={'submit'}><Save
                                                    sx={{width: 16, height: 16}}/></IconButton>
                                                :
                                                !grouped &&
                                                <Box display={"inline"}>
                                                    <IconButton onClick={() => editRow(row)}>
                                                        <EditIcon sx={{width: 16, height: 16}}/>
                                                    </IconButton>
                                                    <IconButton onClick={() => deleteItem(row.id)}>
                                                        <DeleteIcon sx={{width: 16, height: 16}}/>
                                                    </IconButton>
                                                    <IconButton onClick={() => cloneItem(row.id)}>
                                                        <ContentCopyIcon sx={{width: 16, height: 16}}/>
                                                    </IconButton>
                                                </Box>
                                            }
                                        </TableCell>
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </PaginatedComponent>
        </form>
    )
}
